import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { SharedKernel } from '@/shell';
import { useAuctionEvent, EventLifecycleFrame } from '@/shell';
import type { MyBidsInvoiceSnapshot } from '@/shell';
import type {
  BidderItemSummary,
  BidderInvoiceTotals,
  BidderInvoicePaymentContext,
  BidderOutstandingOffer,
  BidderPayment,
  BidderReleasedLot,
  Invoice,
  ItemHighBidChangedFrame,
  MyBidsResponse,
  MyBidsStatus,
  WsClientFrame,
} from '@neon-one/auctions-types';
import { ApiError } from '@neon-one/auctions-types';

import { myBidsApi } from './api';
import { invoiceTrackRank, pickTrackedInvoiceItem } from './invoice-tracking';

export type Kernel = SharedKernel;
const INVOICE_RELEASED_FRAME = 'invoice.released';

const InvoiceEvent = { Created: 'invoice.created', Processing: 'invoice.processing',
  Paid: 'invoice.paid', Failed: 'invoice.failed', Refunded: 'invoice.refunded', Voided: 'invoice.voided' } as const;
const INVOICE_MONEY_EVENTS: ReadonlySet<string> = new Set(Object.values(InvoiceEvent));

function paymentContextOf(value: BidderInvoicePaymentContext | null | undefined): BidderInvoicePaymentContext {
  return { paymentState: value?.paymentState, pendingRefundCents: value?.pendingRefundCents,
    owedBackCents: value?.owedBackCents, oldestRefundFailedAt: value?.oldestRefundFailedAt,
    organizationName: value?.organizationName };
}

export const useMyBidsStore = defineStore('myBids', () => {
  const winning = ref<BidderItemSummary[]>([]);
  const losing = ref<BidderItemSummary[]>([]);
  const won = ref<BidderItemSummary[]>([]);
  const lost = ref<BidderItemSummary[]>([]);
  const released = ref<BidderReleasedLot[]>([]);
  // GT1-442 (bid-void-design.md §6.2) — items where an organizer removed
  // every bid the bidder had and NO active (non-voided) bid survives.
  // Server-computed: an item with a surviving active bid stays in its
  // active bucket and never lands here, so a dual-bid partial void and a
  // re-bid after removal both bucket normally with no client re-derivation.
  const removed = ref<BidderItemSummary[]>([]);
  const spendingCents = ref<number>(0);
  const invoiceTotals = ref<BidderInvoiceTotals | null>(null);
  // Offers addressed to this bidder that she has not answered yet. The API is
  // the ONLY legal source for them (frontend-structure.md §4.8) — the wins
  // surface must not reach into features/recovery for live offer state.
  //
  // Deliberately NOT folded into spendingCents or the invoice total: an offer
  // she has not accepted moves no money, so it owes nothing and belongs above
  // the balance, never inside it.
  const outstandingOffers = ref<BidderOutstandingOffer[]>([]);
  // Settled charges, newest first. Charge grain: a bidder who settled twice has
  // two entries, which is the whole point of showing them at all.
  const payments = ref<BidderPayment[]>([]);
  const state = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
// The failure's HTTP status, so a page can tell a dead session (401/403 — the
// server will keep refusing, and only signing in again helps) from a transient
// one (retrying is the whole advice). Null when the failure carried no status.
const errorStatus = ref<number | null>(null);
  const error = ref<string | null>(null);

  // Tracks items that a WS bid.outbid frame has moved to losing since the last
  // successful adopt(). Used by adopt() to guard against stale HTTP snapshots
  // arriving after a real-time outbid event. Cleared after each adopt().
  // Items and bundles are separate PK spaces, so bundles get their own set.
  const wsOutbidItemIds = new Set<number>();
  const wsOutbidBundleIds = new Set<number>();

  // GT1-442 — bid ids of void actions already handled by applyFrame. One void
  // action publishes the same frame on several channels; without this the
  // bidder + event copies would each fire a refetch.
  const handledVoidBidIds = new Set<number>();

  // The authenticated bidder's own public UUID, populated by adopt() from
  // the API response. Used in item.high_bid_changed to detect outbids when
  // bid.outbid is missed (e.g., during the WS subscribe handshake gap).
  // Public UUID — internal DB ids are never exposed on the wire.
  const myBidderUuid = ref<string | null>(null);

  const invoiceUuid = ref<string | null>(null);
  const invoiceStatus = ref<Invoice['paymentStatus'] | null>(null);
  const invoiceCheckoutMode = ref<Invoice['checkoutMode'] | null>(null);
  const invoicePaymentMethod = ref<Invoice['paymentMethod'] | null>(null);
  const invoicePaymentReference = ref<Invoice['paymentReference'] | null>(null);
  const invoiceRefundedCents = ref(0);
  const invoiceTotalCents = ref<number | null>(null);
  const invoicePaymentContext = ref<BidderInvoicePaymentContext>({});

  function trackInvoice(uuid: string | null, status: Invoice['paymentStatus'] | null): void {
    if (invoiceUuid.value !== uuid) {
      invoiceCheckoutMode.value = null;
      invoicePaymentMethod.value = null;
      invoicePaymentReference.value = null;
      invoiceRefundedCents.value = 0;
      invoiceTotalCents.value = null;
      invoicePaymentContext.value = {};
    }
    invoiceUuid.value = uuid;
    invoiceStatus.value = status;
  }

  const totalBidsCount = computed(
    () =>
      winning.value.length +
      losing.value.length +
      won.value.length +
      lost.value.length +
      removed.value.length
  );

  // Cross-feature lookup used by catalog + wishlist cards to render a
  // "Winning" / "Won" / "Outbid" pill and gate the Bid CTA on items the
  // bidder already has a position on. Items and bundles are separate PK
  // spaces (see PublicItemSummary), so callers pass both fields explicitly
  // (exactly one is non-null, mirroring the row being looked up) — matching
  // only the field that's actually set rules out a false match against an
  // unrelated same-numbered item/bundle.
  function getBidState(itemId: number | null, bundleId: number | null): 'winning' | 'outbid' | 'won' | null {
    const matches = (i: BidderItemSummary) =>
      (itemId != null && i.itemId === itemId) || (bundleId != null && i.bundleId === bundleId);
    if (won.value.some(matches)) return 'won';
    if (winning.value.some(matches)) return 'winning';
    if (losing.value.some(matches)) return 'outbid';
    if (lost.value.some(matches)) return 'outbid';
    // `removed` is deliberately NOT consulted: the bidder holds no active bid
    // on that item, so catalog + wishlist cards must render no position pill
    // and leave the Bid CTA in its ordinary state — a re-bid after removal is
    // an ordinary placement with no penalty (bid-void-design.md §6.3).
    return null;
  }

  // When event is null (before load), default to post-close so we don't
  // flash live-bidding UI for an auction whose status hasn't been confirmed.
  const mode = computed<'pre-close' | 'post-close'>(() => {
    const auctionEvent = useAuctionEvent();
    return auctionEvent.event?.status === 'open' ? 'pre-close' : 'post-close';
  });

  // Assembled invoice snapshot for design templates (PayBanner, etc.).
  const invoice = computed<MyBidsInvoiceSnapshot | null>(() => {
    if (!invoiceUuid.value || !invoiceStatus.value) return null;
    const wonItemsCents = won.value.filter(item => item.invoiceUuid === invoiceUuid.value)
      .reduce((sum, item) => sum + item.currentHighBidCents, 0);
    return {
      uuid: invoiceUuid.value,
      totalCents: invoiceTotalCents.value ?? wonItemsCents,
      ...invoicePaymentContext.value,
      checkoutMode: invoiceCheckoutMode.value,
      pendingRefundCents: invoicePaymentContext.value.pendingRefundCents ?? 0,
      refundedCents: invoiceRefundedCents.value,
      paymentStatus: invoiceStatus.value,
      paymentMethod: invoicePaymentMethod.value,
      paymentReference: invoicePaymentReference.value,
    };
  });

  function adopt(payload: MyBidsResponse): void {
    // Guard against the cellular race: a bid.outbid WS frame can arrive and
    // move an item from winning→losing *before* a reconnect-triggered HTTP
    // refetch response lands (snapshot taken before the bid committed).
    // Only guard items that WS explicitly moved to losing since the last
    // adopt() — NOT all items currently in losing.value, which would
    // incorrectly block items the bidder just won from being promoted.
    const wsLosing = new Set(wsOutbidItemIds);
    const wsLosingBundles = new Set(wsOutbidBundleIds);
    const isWsLosing = (i: BidderItemSummary): boolean =>
      (i.itemId != null && wsLosing.has(i.itemId)) ||
      (i.bundleId != null && wsLosingBundles.has(i.bundleId));
    const redirected = payload.winning
      .filter(isWsLosing)
      .map(i => ({ ...i, myBidsStatus: 'losing' as const }));
    winning.value = payload.winning.filter(i => !isWsLosing(i)).map(i => ({ ...i }));
    losing.value = [...payload.losing.map(i => ({ ...i })), ...redirected];
    won.value = payload.won.map(i => ({ ...i }));
    lost.value = payload.lost.map(i => ({ ...i }));
    released.value = (payload.released ?? []).map(row => ({ ...row, lot: { ...row.lot },
      ...(row.refund ? { refund: { ...row.refund } } : {}) }));
    // `?? []` tolerates a server that predates the GT1-442 bucket rather than
    // blanking the page on an older deploy.
    removed.value = (payload.removed ?? []).map(i => ({ ...i }));
    // Same tolerance for the offer summary: an older deploy simply has no
    // offers to show, which is the correct rendering, not an error.
    outstandingOffers.value = (payload.outstandingOffers ?? []).map(o => ({ ...o }));
    // `?? []` tolerates a server that predates the payments array rather than
    // blanking the page on an older deploy.
    payments.value = (payload.payments ?? []).map(p => ({ ...p, lines: p.lines.map(l => ({ ...l })) }));
    // Subtract redirected items' amounts from the server spending total
    // (they are no longer in winning, so their cents shouldn't count).
    const redirectedCents = redirected.reduce((sum, r) => sum + r.currentHighBidCents, 0);
    spendingCents.value = payload.spendingCents - redirectedCents;
    invoiceTotals.value = payload.invoiceTotals ? { ...payload.invoiceTotals } : null;
    // Capture the bidder's own public UUID for item.high_bid_changed comparison.
    myBidderUuid.value = payload.bidderUuid;
    // Clear after adopt so the next load cycle starts with a clean slate.
    wsOutbidItemIds.clear();
    wsOutbidBundleIds.clear();
    // Sync invoice state from won items on EVERY adopt (GT1-343). The backend
    // populates BidderItemSummary.invoiceUuid + invoicePaymentStatus for won
    // items once invoice generation completes, so /me/bids is authoritative
    // for paid state on first paint. 'pending' is only a first-paint
    // placeholder for older payloads without invoicePaymentStatus; loadInvoice()
    // re-syncs the real status (+ checkoutMode) as a freshness pass so a
    // checkout completed since the snapshot is never pinned to stale 'pending'.
    //
    // A bidder can hold several invoices per auction once lots are reassigned
    // to her after close (PRD invariant 3), so the row that decides which
    // invoice this page follows is chosen by how much it still wants from her
    // — never by payload order. Picking the first row would let a receipt she
    // already settled hide the invoice she now owes.
    const wonInvoiceItem = pickTrackedInvoiceItem(payload.won);
    const wonInvoiceUuid = wonInvoiceItem?.invoiceUuid ?? null;
    // Nothing won on any invoice any more (released, voided): stop following the old one,
    // or its banner stays up over an empty page.
    if (wonInvoiceUuid === null && invoiceUuid.value !== null && payload.invoiceTotals != null) {
      trackInvoice(null, null);
      invoicePaymentContext.value = paymentContextOf(null);
    }
    if (wonInvoiceUuid !== null) {
      // The followed invoice's own row in this payload is fresher than the status the page
      // remembers. Rank against it: an invoice paid since it last declined must not keep
      // outranking the invoice that is declined now.
      const trackedRow = payload.won.find(item => item.invoiceUuid === invoiceUuid.value);
      if (trackedRow?.invoicePaymentStatus != null) invoiceStatus.value = trackedRow.invoicePaymentStatus;
      if (invoiceUuid.value === null) {
        trackInvoice(wonInvoiceUuid, wonInvoiceItem?.invoicePaymentStatus ?? 'pending');
      } else if (invoiceUuid.value === wonInvoiceUuid && wonInvoiceItem?.invoicePaymentStatus != null) {
        // Same invoice, fresher server snapshot — adopt its status.
        invoiceStatus.value = wonInvoiceItem.invoicePaymentStatus;
      } else if (
        invoiceTrackRank(wonInvoiceItem?.invoicePaymentStatus) > invoiceTrackRank(invoiceStatus.value)
        || (payload.invoiceTotals != null && !payload.won.some(item => item.invoiceUuid === invoiceUuid.value))
      ) {
        // Aggregate-capable responses supersede a disappeared selection;
        // older payloads retain the one-way open-invoice freshness guard.
        trackInvoice(wonInvoiceUuid, wonInvoiceItem?.invoicePaymentStatus ?? 'pending');
      }
      if (invoiceUuid.value === wonInvoiceUuid) {
        invoicePaymentContext.value = paymentContextOf(wonInvoiceItem);
      }
      // The invoice fetch has the last word on the followed invoice's status, whichever
      // invoice the payload ranked first: a payload taken before a frame (a decline, a
      // WS invoice.created) can report an older one.
      if (invoiceUuid.value !== null) void loadInvoice(invoiceUuid.value);
    }
  }

  // In-flight guard so repeated adopt() calls (navigation, reconnect refresh)
  // cannot stack duplicate invoice fetches or loop.
  let invoiceFetchInFlightUuid: string | null = null;

  // The fetched status is fresher than the won rows the page ranked by, so it is written
  // back to them, and an invoice that now asks for more takes over: the one followed may
  // have been paid since the payload was taken. Writing it back is what ends the hand-off;
  // a row left stale would win the next ranking and send the page back to it.
  function retargetIfOutranked(uuid: string, status: Invoice['paymentStatus']): void {
    for (const row of won.value) if (row.invoiceUuid === uuid) row.invoicePaymentStatus = status;
    const best = pickTrackedInvoiceItem(won.value);
    if (!best?.invoiceUuid || best.invoiceUuid === uuid) return;
    if (invoiceTrackRank(best.invoicePaymentStatus) <= invoiceTrackRank(status)) return;
    trackInvoice(best.invoiceUuid, best.invoicePaymentStatus ?? 'pending');
    invoicePaymentContext.value = paymentContextOf(best);
    void loadInvoice(best.invoiceUuid);
  }

  async function loadInvoice(uuid: string): Promise<void> {
    if (invoiceFetchInFlightUuid === uuid) return;
    invoiceFetchInFlightUuid = uuid;
    try {
      const inv = await myBidsApi.fetchInvoice(uuid);
      // Guard: a newer invoice.created frame may have arrived while this fetch was in-flight.
      if (invoiceUuid.value !== uuid) return;
      invoiceCheckoutMode.value = inv.checkoutMode;
      // Sync actual status — needed for the page-reload-after-close path where
      // adopt() seeds 'pending' as a placeholder before the real status is known.
      invoiceStatus.value = inv.paymentStatus;
      invoicePaymentMethod.value = inv.paymentMethod;
      invoicePaymentReference.value = inv.paymentReference;
      invoiceRefundedCents.value = inv.refundedCents ?? 0;
      invoiceTotalCents.value = inv.totalCents;
      invoicePaymentContext.value = paymentContextOf(inv);
      retargetIfOutranked(uuid, inv.paymentStatus);
    } catch {
      // non-critical — checkoutMode falls back to null; banner still shows pending state
    } finally {
      if (invoiceFetchInFlightUuid === uuid) invoiceFetchInFlightUuid = null;
    }
  }

  // In-flight guard: App.vue's eager shell-level load (subscribeBidder) and
  // MyBidsPage's own mount-time load can fire concurrently. Without this,
  // the second call resets state back to 'loading' mid-flight, which (on
  // higher-latency mobile connections) extends the window where the page
  // renders its blank shell instead of content (GT1-358).
  let loadInFlight: Promise<void> | null = null;

  async function load(options: { silent?: boolean } = {}): Promise<void> {
    if (loadInFlight) return loadInFlight;
    const silent = options.silent === true;
    loadInFlight = (async () => {
      // A silent reload keeps the last-known content on screen instead of
      // dropping back to the skeleton. Used for WS-driven refetches (GT1-442
      // bid.voided) where the bidder is already looking at the list — a
      // skeleton flash there reads as a page reset, not a live update.
      if (!silent) state.value = 'loading';
      error.value = null;
      errorStatus.value = null;
      try {
        adopt(await myBidsApi.fetch());
        state.value = 'success';
      } catch (e) {
        // A failed silent refresh leaves the previous snapshot visible; only a
        // foreground load escalates to the page-level error state.
        if (!silent) state.value = 'error';
        error.value = e instanceof Error ? e.message : 'Failed to load bids';
        errorStatus.value = e instanceof ApiError ? e.status : null;
        // Surfaced instead of silently swallowed — a fire-and-forget caller
        // (App.vue's eager load) would otherwise leave failures invisible.
        console.error('[myBids] load failed', e);
      } finally {
        loadInFlight = null;
      }
    })();
    return loadInFlight;
  }

    // Forces a fresh snapshot even when a load is already in flight: that
    // in-flight response may have been taken by the server BEFORE the void
    // committed, so reusing it would show stale buckets.
    //
    // Coalesced: a burst of frames (a charge's processing and paid, a refund) queues one
    // reload, not one per frame. A frame that lands once the queued reload has started
    // queues the next one, since that reload may predate it.
    let reloadQueued: Promise<void> | null = null;
    function reloadAfter(): Promise<void> {
        if (reloadQueued) return reloadQueued;
        reloadQueued = (async () => {
            await loadInFlight;
            reloadQueued = null;
            await load({ silent: true });
        })();
        return reloadQueued;
    }

  /**
   * Move a row from one section to another in response to a WS frame.
   * Used when bid.outbid takes a winning row to losing without a refetch.
   */
  function moveItemBy(matches: (row: BidderItemSummary) => boolean, to: MyBidsStatus): void {
    const sectionFor = (s: MyBidsStatus) => {
      switch (s) {
        case 'winning':
          return winning;
        case 'losing':
          return losing;
        case 'won':
          return won;
        case 'lost':
          return lost;
        // An admin-released row leaves the bidder's active lists the same way
        // a removed one does.
        case 'removed':
        case 'released':
          return removed;
      }
    };
    let target: BidderItemSummary | null = null;
    for (const status of ['winning', 'losing', 'won', 'lost', 'removed'] as const) {
      const section = sectionFor(status);
      const idx = section.value.findIndex(matches);
      if (idx !== -1) {
        target = section.value[idx] ?? null;
        section.value.splice(idx, 1);
        break;
      }
    }
    if (!target) return;
    const moved: BidderItemSummary = { ...target, myBidsStatus: to };
    sectionFor(to).value.push(moved);
  }

  function moveItem(itemId: number, to: MyBidsStatus): void {
    moveItemBy(row => row.itemId === itemId, to);
  }

  // Keeps the pre-computed minimumNextBidCents field in sync whenever a WS
  // frame updates currentHighBidCents, so any consumer reading it directly
  // (not just BidCard's dynamic derivation) stays correct (GT1-359).
  function syncMinimumNextBid(row: BidderItemSummary): void {
    row.minimumNextBidCents = row.currentHighBidCents + row.bidIncrementCents;
  }

  function applyFrame(frame: WsClientFrame): void {
    if (INVOICE_MONEY_EVENTS.has(frame.type)) void reloadAfter();
    // GT1-442 (bid-void-design.md §6) — an organizer removed one or more bids.
    // One void action fans out on bidder:{uuid}, item:{id} and event:{uuid},
    // so the same action can arrive here twice (My Bids subscribes to the
    // bidder + event channels); `handledVoidBidIds` keeps handling idempotent.
    //
    // Whether the item belongs in the Removed bucket depends on facts only the
    // server has (does ANY active bid of mine survive on this item?), so a
    // frame that touched MY bid triggers an authoritative silent refetch rather
    // than a local guess. Someone else's void only moves the price, which is
    // patched in place; standing changes arrive via item.high_bid_changed.
    if (frame.type === 'bid.voided') {
      const payload = frame.payload as
        | {
            bidId?: number;
            voidedBidIds?: number[];
            itemId?: number | null;
            bundleId?: number | null;
            bidderUuid?: string;
            recalculatedHighBidCents?: number;
          }
        | undefined;
      if (!payload) return;
      const channel = (frame as { channel?: string }).channel ?? '';
      // A bidder-channel frame is targeted at this bidder by definition; the
      // event-channel copy is identified via the payload's public bidderUuid.
      const isMine =
        channel.startsWith('bidder:') ||
        (payload.bidderUuid != null &&
          myBidderUuid.value !== null &&
          payload.bidderUuid === myBidderUuid.value);
      const actionKey = payload.bidId ?? payload.voidedBidIds?.[0] ?? null;
      if (actionKey !== null) {
        if (handledVoidBidIds.has(actionKey)) return;
        handledVoidBidIds.add(actionKey);
      }
      // Patch the price on whichever section holds the row so the amount and
      // the derived Bid CTA don't read stale while the refetch is in flight.
      if (payload.recalculatedHighBidCents != null) {
        const targetItemId = payload.itemId ?? null;
        const targetBundleId = payload.bundleId ?? null;
        for (const section of [winning, losing, won, lost, removed]) {
          const row = section.value.find(
            r =>
              (targetItemId != null && r.itemId === targetItemId) ||
              (targetBundleId != null && r.bundleId === targetBundleId),
          );
          if (row) {
            row.currentHighBidCents = payload.recalculatedHighBidCents;
            syncMinimumNextBid(row);
            break;
          }
        }
      }
      // NOTE: payload.reason is an admin-only free-text field. It is never read
      // here and must never reach a bidder surface (§6.1 / Q1).
      if (isMine) void reloadAfter();
      return;
    }
    if (frame.type === 'bid.outbid') {
      const payload = frame.payload as
        | {
            itemId?: number | null;
            bundleId?: number | null;
            currentHighBidCents?: number;
          }
        | undefined;
      // Exactly one of itemId/bundleId is set (mirrors the wire frame) —
      // gating on itemId alone silently drops every bundle outbid (GT1-360).
      const targetItemId = payload?.itemId ?? null;
      const targetBundleId = payload?.bundleId ?? null;
      if (targetItemId == null && targetBundleId == null) return;
      const matches = (r: BidderItemSummary): boolean =>
        (targetItemId != null && r.itemId === targetItemId) ||
        (targetBundleId != null && r.bundleId === targetBundleId);
      const allRows = [...winning.value, ...losing.value];
      const row = allRows.find(matches);
      const wasWinning = winning.value.some(matches);
      if (wasWinning && row) spendingCents.value -= row.currentHighBidCents;
      if (row && payload?.currentHighBidCents != null) {
        row.currentHighBidCents = payload.currentHighBidCents;
        syncMinimumNextBid(row);
      }
      // Do NOT touch row.myLastBidAmountCents — being outbid means someone
      // else placed a bid; this user's last bid is unchanged. The frame's
      // previousAmountCents represents the previous current-high (or in some
      // proxy-ladder paths, the defender's max), not "your last bid amount".
      if (targetItemId != null) wsOutbidItemIds.add(targetItemId);
      if (targetBundleId != null) wsOutbidBundleIds.add(targetBundleId);
      moveItemBy(matches, 'losing');
    }
    // bid.placed on the bidder channel fires when the proxy engine placed a
    // counter-bid on behalf of this bidder (i.e. their max held). Only process
    // bidder-channel frames — event/item-channel bid.placed belongs to other
    // bidders and would corrupt myLastBidAmountCents.
    if (frame.type === 'bid.placed') {
      if (!frame.channel?.startsWith('bidder:')) return;
      const payload = frame.payload as
        | { itemId?: number | null; bundleId?: number | null; amountCents?: number }
        | undefined;
      // Exactly one of itemId/bundleId is set — gating on itemId alone
      // silently dropped every bundle promotion, leaving a stale "Outbid"
      // pill after a successful rebid on a bundle.
      const targetItemId = payload?.itemId ?? null;
      const targetBundleId = payload?.bundleId ?? null;
      if ((targetItemId == null && targetBundleId == null) || payload?.amountCents == null) return;
      const amountCents = payload.amountCents;
      const matches = (r: BidderItemSummary): boolean =>
        (targetItemId != null && r.itemId === targetItemId) ||
        (targetBundleId != null && r.bundleId === targetBundleId);
      const wasWinning = winning.value.some(matches);
      let found = false;
      // `removed` is included: a re-bid after an organizer void is an
      // ordinary placement (bid-void-design.md §6.3), so the row must be
      // able to leave the Removed bucket when the new bid leads.
      for (const section of [winning, losing, won, lost, removed]) {
        const row = section.value.find(matches);
        if (row) {
          if (wasWinning) spendingCents.value -= row.currentHighBidCents;
          row.myLastBidAmountCents = amountCents;
          row.currentHighBidCents = amountCents;
          syncMinimumNextBid(row);
          found = true;
          break;
        }
      }
      if (!found) {
        // First bid on this entity — there is no row to promote yet. Pull
        // the authoritative snapshot so the new position appears without a
        // manual reload (and the catalog card's pill via getBidState).
        void reloadAfter();
        return;
      }
      spendingCents.value += amountCents;
      // Bidder leads again — lift the adopt() outbid guard.
      if (targetItemId != null) wsOutbidItemIds.delete(targetItemId);
      if (targetBundleId != null) wsOutbidBundleIds.delete(targetBundleId);
      moveItemBy(matches, 'winning');
      // Attribution is private My Bids state, not a public bid-frame field.
      // Proxy rows inherit their source max bid, so never infer staff origin.
      // eslint-disable-next-line local-rules/no-floating-side-effect -- Best-effort attribution refresh; the next page mount reloads authoritative rows.
      void reloadAfter();
    }
    if (frame.type === 'item.high_bid_changed') {
      const payload = frame.payload as ItemHighBidChangedFrame['payload'] | undefined;
      if (!payload || (payload.itemId == null && payload.bundleId == null)) return;
      // Exactly one of itemId/bundleId is set (mirrors the wire frame) — a
      // bundle row always carries itemId: null, so matching on itemId alone
      // would match EVERY bundle row against a bundle-scoped frame instead of
      // just the one it names.
      const matchesRow = (r: { itemId: number | null; bundleId?: number | null }): boolean =>
        payload.itemId != null ? r.itemId === payload.itemId : r.bundleId === payload.bundleId;
      const targetItemId = payload.itemId;
      const targetBundleId = payload.bundleId;

      // Check section movement BEFORE updating currentHighBidCents so the old
      // value is still available for spending adjustments (same pattern as bid.outbid).
      // Use != null (loose) to skip frames where currentHighBidderUuid is absent.
      if (myBidderUuid.value !== null && payload.currentHighBidderUuid != null) {
        const iAmWinner = payload.currentHighBidderUuid === myBidderUuid.value;
        const inWinning = winning.value.some(matchesRow);
        const inLosing = losing.value.some(matchesRow);
        if (!iAmWinner && inWinning) {
          // bid.outbid was missed — detect via high_bid_changed.
          // Subtract the OLD currentHighBidCents BEFORE moveItem changes the section.
          const winRow = winning.value.find(matchesRow);
          spendingCents.value -= winRow?.currentHighBidCents ?? 0;
          // Guard next adopt() too — a bundle needs its own guard set just
          // like an item does, or a stale HTTP snapshot landing after this
          // frame can restore an outbid bundle to Winning (see adopt()'s
          // cellular-race comment).
          if (targetItemId != null) wsOutbidItemIds.add(targetItemId);
          if (targetBundleId != null) wsOutbidBundleIds.add(targetBundleId);
          moveItemBy(matchesRow, 'losing');
        } else if (iAmWinner && inLosing) {
          // Proxy counter-bid won — bid.placed on bidder channel handles this
          // normally, but guard the edge case where it's missed too.
          if (targetItemId != null) wsOutbidItemIds.delete(targetItemId);
          if (targetBundleId != null) wsOutbidBundleIds.delete(targetBundleId);
          moveItemBy(matchesRow, 'winning');
          spendingCents.value += payload.currentHighBidCents;
        } else if (iAmWinner && inWinning) {
          // Still winning but bid amount changed (e.g. bid.placed missed during
          // WS subscribe-gap). Adjust spending by the delta before the row is updated.
          const winRow = winning.value.find(matchesRow);
          spendingCents.value += payload.currentHighBidCents - (winRow?.currentHighBidCents ?? 0);
        }
      }
      // Update the bid amount on the row in whichever section it now occupies.
      for (const section of [winning, losing, won, lost]) {
        const row = section.value.find(matchesRow);
        if (row) {
          row.currentHighBidCents = payload.currentHighBidCents;
          syncMinimumNextBid(row);
          break;
        }
      }
    }
        if (frame.type === EventLifecycleFrame.Closed) {
            // Only a normal close sends this, so every high bidder won. Archiving an open
            // auction sends event.archived instead. spendingCents is what they owe at checkout.
            won.value = [...won.value, ...winning.value.map(i => ({ ...i, myBidsStatus: 'won' as const }))];
            lost.value = [...lost.value, ...losing.value.map(i => ({ ...i, myBidsStatus: 'lost' as const }))];
            winning.value = [];
            losing.value = [];
        }
    if (frame.type === INVOICE_RELEASED_FRAME) {
      if (frame.channel !== `bidder:${myBidderUuid.value}`) return;
      // Release actor, cancellation and refund state need the server snapshot;
      // the frame's itemId/bundleId alone cannot decide the bidder's section.
      // eslint-disable-next-line local-rules/no-floating-side-effect -- Best-effort silent refresh; the next page mount reloads authoritative outcomes.
      void reloadAfter();
      return;
    }
    if (frame.type === InvoiceEvent.Created) {
      const p = frame.payload as { invoiceUuid?: string } | undefined;
      if (!p?.invoiceUuid) return;
      trackInvoice(p.invoiceUuid, 'pending');
      void loadInvoice(p.invoiceUuid);
    }
    if (frame.type === InvoiceEvent.Processing) {
      const p = frame.payload as { invoiceUuid?: string } | undefined;
      if (invoiceUuid.value !== null && p?.invoiceUuid !== invoiceUuid.value) return;
      invoiceStatus.value = 'processing';
    }
    if (frame.type === InvoiceEvent.Paid) {
      const p = frame.payload as { invoiceUuid?: string } | undefined;
      if (invoiceUuid.value !== null && p?.invoiceUuid !== invoiceUuid.value) return;
      invoiceStatus.value = 'paid';
      // The frame only carries invoiceUuid/paidAt/transactionId, not the
      // invoice's paymentMethod/paymentReference — refetch so PayBanner can
      // render the correct cash/check/card wording as soon as the charge
      // lands, without waiting on the bids reload (GT1-353). Set invoiceUuid too
      // (loadInvoice's stale-fetch guard compares against it) in case this
      // frame arrives without a preceding invoice.created.
      const uuid = p?.invoiceUuid ?? invoiceUuid.value;
      if (uuid) {
        invoiceUuid.value = uuid;
        void loadInvoice(uuid);
      }
    }
    if (frame.type === InvoiceEvent.Failed) {
      const p = frame.payload as { invoiceUuid?: string } | undefined;
      if (invoiceUuid.value !== null && p?.invoiceUuid !== invoiceUuid.value) return;
      invoiceStatus.value = 'failed';
    }
    if (frame.type === InvoiceEvent.Refunded) {
      const p = frame.payload as { invoiceUuid?: string } | undefined;
      const uuid = p?.invoiceUuid ?? invoiceUuid.value;
      if (invoiceUuid.value !== null && uuid !== invoiceUuid.value) return;
      // The frame only carries the refund delta, not the resulting status or
      // net total — refetch so the banner reflects refunded vs partially-refunded
      // and the correct refunded amount.
      if (uuid) {
        invoiceUuid.value = uuid;
        void loadInvoice(uuid);
      }
    }
  }

  /**
   * Called after this bidder's OWN placement succeeds over HTTP. The WS
   * bid.placed frame on the bidder channel usually promotes the row, but
   * that path can be missed (subscribe-handshake gap, zombie socket) — and
   * a stale wsOutbid guard left over from the outbid that prompted the
   * rebid would make the next adopt() re-demote a server-confirmed win
   * back to "Outbid" (July usability session 7). Clear the guard for the
   * target and pull an authoritative snapshot; the server response, not a
   * local guess, decides the bucket (covers the tie/outbid-immediately
   * case where a successful placement still isn't a lead).
   */
  function noteOwnPlacement(itemId: number | null, bundleId: number | null): void {
    if (itemId != null) wsOutbidItemIds.delete(itemId);
    if (bundleId != null) wsOutbidBundleIds.delete(bundleId);
    for (const section of [winning, losing, removed]) {
        const row = section.value.find(item =>
            (itemId != null && item.itemId === itemId) || (bundleId != null && item.bundleId === bundleId));
        if (row) row.latestBidByStaff = false;
    }
    void reloadAfter();
  }

  // Demo-only: patch invoice state for the DemoStateToggle charge-status
  // cycling feature. Real invoice state flows through WS frames.
  function setMockInvoice(snapshot: MyBidsInvoiceSnapshot | null): void {
    trackInvoice(snapshot?.uuid ?? null, snapshot?.paymentStatus ?? null);
    invoiceTotals.value = null;
    if (!snapshot) return;
    invoiceCheckoutMode.value = snapshot.checkoutMode ?? null;
    invoicePaymentMethod.value = snapshot.paymentMethod;
    invoicePaymentReference.value = snapshot.paymentReference;
    invoiceRefundedCents.value = snapshot.refundedCents ?? 0;
    invoiceTotalCents.value = snapshot.totalCents;
    invoicePaymentContext.value = paymentContextOf(snapshot);
  }

  return {
    winning,
    losing,
    won,
    lost,
    released,
    removed,
    spendingCents,
    invoiceTotals,
    outstandingOffers,
    payments,
    state,
    error,
    errorStatus,
    totalBidsCount,
    mode,
    invoice,
    getBidState,
    load,
    adopt,
    moveItem,
    applyFrame,
    noteOwnPlacement,
    invoiceUuid,
    invoiceStatus,
    invoiceCheckoutMode,
    invoicePaymentMethod,
    invoicePaymentReference,
    setMockInvoice,
  };
});
